import type {DalValue, ComponentWithResolvedDataItems, SchemaService, VariantSplit} from '@wix/document-services-types'
import {VARIANTS_DATA_TYPES, DATA_TYPES} from '@wix/format-converter-schemas'
import _ from 'lodash'
import {REF_ARRAY_DATA_TYPE} from '../../constants'
import type {SingleComponentPluginFunction} from '../../types'
import {
    type ReferredDataItemsIterateeContext,
    createVariantRelation,
    forEachReferredDataItem
} from '@wix/import-export-utils'
import {traverseInnerElementsForVariants} from './traverse'
import {isVariantSplit} from './utils'

const PREFERRED_SINGLE_ITEM_NAMESPACES = new Set([DATA_TYPES.design])

/**
 * Convert VariantSplit back to RefArray format
 * VariantSplit: { default: value, variants: [{value, conditions}] }
 * RefArray: { type: 'RefArray', values: [default, ...VariantRelations] }
 */
export const convertVariantSplitToDataItem = (
    {default: defaultValue, variants}: VariantSplit,
    namespace: string,
    componentId: string
): DalValue => {
    if (!variants || (variants.length === 0 && PREFERRED_SINGLE_ITEM_NAMESPACES.has(namespace))) {
        return defaultValue
    }

    const refArray = {
        type: REF_ARRAY_DATA_TYPE,
        values: variants.map(variant => createVariantRelation(variant.conditions, componentId, variant.value))
    }

    if (defaultValue) {
        refArray.values.unshift(defaultValue)
    }

    return refArray
}

/**
 * Process variant-supporting namespaces on an innerElement
 * Converts VariantSplit structures back to RefArray format
 */
const unpackInnerElementVariants = (element: Record<string, unknown>, componentId: string): void => {
    _.forOwn(VARIANTS_DATA_TYPES, (namespace: string) => {
        const featureData = element[namespace]
        if (!featureData) {
            return
        }

        // VariantSplit on the namespace (e.g., element.style = {default, variants})
        if (isVariantSplit(featureData)) {
            element[namespace] = convertVariantSplitToDataItem(featureData, namespace, componentId)
        }
    })
}

/**
 * Unpack function that converts VariantSplit back to RefArray
 * Processes both component-level data and innerElements
 */
export const unpack: SingleComponentPluginFunction = (
    component: ComponentWithResolvedDataItems,
    schemaService: SchemaService
): void => {
    // Process component-level variant namespaces
    _.forOwn(VARIANTS_DATA_TYPES, (namespace: string) => {
        const {structure: componentStructure, data: componentData} = component
        const featureData = componentData[namespace]
        if (!featureData) {
            return
        }

        forEachReferredDataItem(
            schemaService,
            featureData,
            namespace,
            ({
                item,
                namespace: itemNamespace,
                itemRefInfo,
                ownerItem = componentData
            }: ReferredDataItemsIterateeContext) => {
                if (itemRefInfo.isRelationalSplit) {
                    _.set(
                        ownerItem,
                        itemRefInfo.path,
                        convertVariantSplitToDataItem(item, itemNamespace, componentStructure.id)
                    )
                }
            }
        )
    })

    // Process innerElements variant namespaces
    traverseInnerElementsForVariants(component, ({element, componentId}) => {
        unpackInnerElementVariants(element, componentId)
    })
}
